import { useMutation, useQuery } from "@apollo/client";
import {
  OfficeBuildingIcon,
  PencilIcon,
  PlusCircleIcon,
  PlusIcon,
  TrashIcon,
  ViewGridAddIcon,
} from "@heroicons/react/outline";
import { compact, max, omit, sortBy } from "lodash";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Prompt } from "react-router-dom";
import {
  CompetencyCriteriaUniqueness,
  DeleteOrgWideCareerLevelMutation,
  DeleteOrgWideCareerLevelMutationVariables,
  GetDepartmentTitleQueryQuery,
  GetDepartmentTitleQueryQueryVariables,
  GetOrganizationCareerTracksQuery,
  GetOrganizationCareerTracksQueryVariables,
  GetOrganizationCompetenciesQuery,
  GetOrganizationCompetenciesQueryVariables,
  SaveOrgWideCompetencyMutation,
  SaveOrgWideCompetencyMutationVariables,
} from "types/graphql-schema";
import { v4 as uuidv4 } from "uuid";

import TeamEditDialog from "@apps/org-settings/components/team-edit-dialog";
import createOrUpdateOrgWideCompetencyMutation from "@apps/org-settings/graphql/create-or-update-org-wide-competency-mutation";
import deleteCareerLevelMutation from "@apps/org-settings/graphql/delete-career-level-mutation";
import getDepartmentTitleQuery from "@apps/org-settings/graphql/get-department-title-query";
import getOrganizationCareerTracksQuery from "@apps/org-settings/graphql/get-organization-career-tracks-query";
import getOrganizationCompetenciesQuery from "@apps/org-settings/graphql/get-organization-competencies-query";
import useLabel from "@apps/use-label/use-label";
import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";

import SettingsPageHeader from "../settings-page-header";
import AppliesToTag from "./applies-to-tag";
import AutosaveStatus from "./autosave-status";
import useCareerPermissions, {
  LEAD_DELETE_ERROR_MATCHES,
} from "./career-permissions";
import TitleRow from "./career-track-form/title-row";
import CareerTrackViewSwitcher from "./career-track-view-switcher";
import CompetencyFormModal from "./competency-form-modal";
import { getCompetencySharing } from "./competency-sharing";
import CompetencySharingChip from "./competency-sharing-chip";
import { sectionHeaderToneClassName } from "./competency-sharing-tone";
import CriteriaEditor from "./criteria-editor";
import EditModeToggle from "./edit-mode-toggle";
import {
  CompetencyScopeSection,
  addLevelColClassName,
  bodyRowLineClassName,
  changedCriteria,
  criteriaUuid,
  getCompetencyScopeSection,
  getRolesAndCompetenciesBreadcrumbs,
  isDepartmentCompetency,
  keepBlankCriteriaAfterSave,
  newCareerTrackPath,
  roleColWidth,
  roleLevelIterator,
  savedCriteriaByCompetencyId,
  stickyCellBottomLineClassName,
  stickyColWidth,
  stickyHeaderBorderClassName,
  withSavedId,
} from "./helpers";
import LevelDeleteChoiceDialog from "./level-delete-choice-dialog";
import { summarizeLevelDeletion } from "./level-delete-count";
import NoTracksNotice from "./no-tracks-notice";
import { BuildWithAiButton } from "./role-framework-cta";
import ScopeSectionIcon from "./scope-section-icon";
import StickyColumnShadow from "./sticky-column-shadow";
import { CompetencyCriteriaInputWithUUID } from "./types";
import useAutosave from "./use-autosave";
import useKeepCriteriaEditsSince from "./use-keep-criteria-edits-since";
import usePendingDeletes from "./use-pending-deletes";
import useRolesAskAiSidebar from "./use-roles-ask-ai-sidebar";
import useRolesEditMode from "./use-roles-edit-mode";

// Level-by-level editor for competencies whose criteria are shared across
// roles: the company's own, and — when a department is being viewed — that
// department's, so they can be filled in without opening a career track.
const AllRolesCompetencies = ({
  organizationId,
  teamId,
  showAllCompetencies,
}: {
  organizationId: number;
  // Set when viewing one department rather than the whole organization.
  teamId?: number;
  // Every competency, whoever it reaches, rather than the company-wide ones.
  showAllCompetencies?: boolean;
}) => {
  const label = useLabel();
  const link = useLink();
  const askAiSidebar = useRolesAskAiSidebar();
  const { canManageLadder, ledTeamIds, canCreate, canEditCompetency } =
    useCareerPermissions();
  // A department lead makes competencies only in the departments they run:
  // from any view but another department's, and the form keeps them there.
  const canCreateHere =
    canManageLadder || (teamId ? ledTeamIds.includes(teamId) : canCreate);
  const { isEditing: isEditMode, setIsEditing } = useRolesEditMode();
  // Someone with nothing they could change here only reads.
  const isEditing = isEditMode && canCreate;
  const { data: teamData } = useQuery<
    GetDepartmentTitleQueryQuery,
    GetDepartmentTitleQueryQueryVariables
  >(getDepartmentTitleQuery, {
    variables: { teamId: teamId ?? 0 },
    skip: !teamId,
  });
  const teamTitle = teamData?.team?.title;
  const [isFormDirty, setIsFormDirty] = useState(false);
  // Levels the ladder should show even before anything is written on them.
  const [minLevelCount, setMinLevelCount] = useState(1);
  // A level being cleared or deleted: the table is read only and every level
  // action is off until it's done, so nothing typed meanwhile is lost when its
  // edits are reset, and a second delete can't take the level that moved down
  // into its place. The ref answers at once, before a render.
  const [isChangingLevel, setIsChangingLevel] = useState(false);
  const isChangingLevelRef = useRef(false);
  const setChangingLevel = (value: boolean) => {
    isChangingLevelRef.current = value;
    setIsChangingLevel(value);
  };
  const [isCreatingDepartment, setIsCreatingDepartment] = useState(false);
  // Creating one from a section starts it with that section's reach.
  const [creatingCompetency, setCreatingCompetency] = useState<{
    title: string;
    scope: "company" | "shared" | "track";
  } | null>(null);
  const [editingCompetencyId, setEditingCompetencyId] = useState<number | null>(
    null,
  );
  // Unsaved criteria edits by competency id: the competency's full list.
  const [criteriaEdits, setCriteriaEdits] = useState<
    Record<number, CompetencyCriteriaInputWithUUID[]>
  >({});
  // Criteria a bin button took out, by competency id, for the next save to
  // send: a save deletes only the ids it names.
  const pendingDeletes = usePendingDeletes();

  const {
    data: competenciesData,
    loading: isCompetenciesLoading,
    refetch: refetchCompetencies,
  } = useQuery<
    GetOrganizationCompetenciesQuery,
    GetOrganizationCompetenciesQueryVariables
  >(getOrganizationCompetenciesQuery, {
    fetchPolicy: "no-cache",
    nextFetchPolicy: "no-cache",
    variables: { organizationId },
  });
  useKeepCriteriaEditsSince({
    data: competenciesData,
    setEdits: setCriteriaEdits,
    pendingDeletes,
    deleteList: "",
  });

  // Levels come from the tracks, so this view has a column per level in use.
  const {
    data: careerTracksData,
    loading: isCareerTracksLoading,
    refetch: refetchCareerTracks,
  } = useQuery<
    GetOrganizationCareerTracksQuery,
    GetOrganizationCareerTracksQueryVariables
  >(getOrganizationCareerTracksQuery, {
    fetchPolicy: "cache-and-network",
    // Deleting a level names who holds a role on it.
    variables: { organizationId, withHolders: true },
  });

  const competenciesLabel = label("competency", {
    capitalize: true,
    pluralize: true,
  });

  // The competencies every role in the organization is held to, or in a
  // department's view, the ones its tracks inherit as well. Narrowest first,
  // the same order a career track's table reads in.
  const sections = useMemo(() => {
    const all = competenciesData
      ? assertEdgesNonNull(competenciesData.competencies).filter(
          (competency) =>
            // Criteria written per level, without a role to hang them on.
            // Every competency shows in the all view; ones written per role
            // say so in their cells.
            showAllCompetencies ||
            competency.criteriaUniqueness ===
              CompetencyCriteriaUniqueness.SameAcrossRoles,
        )
      : [];
    const byTitle = sortBy(all, (competency) => competency.title.toLowerCase());
    // Every role, or every role of one type: the track table's rule too.
    const companyWide = byTitle.filter(
      (competency) => getCompetencyScopeSection(competency) === "company",
    );
    const company = {
      key: "company" as const,
      title: `Company-Wide ${competenciesLabel}`,
      competencies: companyWide,
    };
    if (showAllCompetencies) {
      // The track table's three sections, narrowest first. Only Company-Wide
      // shows empty: the others name things there is no way to add here.
      const inScope = (scope: CompetencyScopeSection) =>
        byTitle.filter(
          (competency) => getCompetencyScopeSection(competency) === scope,
        );
      return [
        {
          key: "track" as const,
          title: `Single-Track ${competenciesLabel}`,
          competencies: inScope("track"),
        },
        {
          key: "shared" as const,
          title: `Shared ${competenciesLabel}`,
          competencies: inScope("shared"),
        },
        company,
      ].filter(
        (section) =>
          section.key === "company" || section.competencies.length > 0,
      );
    }
    if (!teamId) {
      return [company];
    }
    return [
      {
        // Shared rather than a scope of its own: every track in the
        // department is held to these.
        key: "shared" as const,
        title: `${
          teamTitle ?? label("team", { capitalize: true })
        } ${competenciesLabel}`,
        competencies: byTitle.filter((competency) =>
          isDepartmentCompetency(competency, teamId),
        ),
      },
      company,
    ];
  }, [
    competenciesData,
    competenciesLabel,
    label,
    showAllCompetencies,
    teamId,
    teamTitle,
  ]);

  const competencies = useMemo(
    () => sections.flatMap((section) => section.competencies),
    [sections],
  );

  const criteriaByCompetencyId = useMemo(
    () =>
      Object.fromEntries(
        competencies.map((competency) => [
          competency.id,
          criteriaEdits[competency.id] ??
            assertEdgesNonNull(competency.criteria).map((criteria) => ({
              id: criteria.id,
              level: criteria.level,
              content: criteria.content,
              inReview: criteria.inReview,
              uuid: criteriaUuid(criteria.id),
            })),
        ]),
      ),
    [competencies, criteriaEdits],
  );

  const levelCount = useMemo(() => {
    const trackLevels = careerTracksData
      ? assertEdgesNonNull(careerTracksData.careerTracks).flatMap((track) =>
          assertEdgesNonNull(track.roles).map((role) => role.level),
        )
      : [];
    const criteriaLevels = Object.values(criteriaByCompetencyId).flatMap(
      (criteria) => criteria.map((item) => item.level ?? 1),
    );
    return Math.max(
      max([...trackLevels, ...criteriaLevels]) ?? 1,
      minLevelCount,
      1,
    );
  }, [careerTracksData, criteriaByCompetencyId, minLevelCount]);

  const [saveCompetency] = useMutation<
    SaveOrgWideCompetencyMutation,
    SaveOrgWideCompetencyMutationVariables
  >(createOrUpdateOrgWideCompetencyMutation, {
    context: { errorMatches: LEAD_DELETE_ERROR_MATCHES },
  });

  const handleSave = useCallback(async () => {
    setIsFormDirty(false);
    const edited = Object.entries(criteriaEdits);
    // Read with the edits above: anything deleted from here on is the next
    // save's.
    const deletes = pendingDeletes.snapshot();
    // Ids saves gave what they created: this save may be built from a render
    // that hasn't caught up with them (see withSavedId).
    const savedIds = pendingDeletes.savedIds();
    const saved = savedCriteriaByCompetencyId(competenciesData);
    const results = await Promise.allSettled(
      edited.map(async ([competencyId, criteria]) => {
        const sentDeleteIds = deletes[competencyId] ?? [];
        // Only new and changed criteria: the rest stay as they are.
        const changed = changedCriteria(
          criteria.map((item) => withSavedId(item, savedIds)),
          saved.get(Number(competencyId)) ?? [],
        );
        if (changed.length === 0 && sentDeleteIds.length === 0) {
          return;
        }
        const response = await saveCompetency({
          variables: {
            competencyId: Number(competencyId),
            criteria: changed.map((item) =>
              omit(item, "uuid", "__typename", "inReview"),
            ),
            deleteCriteriaIds: sentDeleteIds,
          },
        });
        // Before the refetch, which can fail: the retry then updates what
        // this created rather than creating it again.
        pendingDeletes.learnCreatedIds(
          response.data?.createOrUpdateCompetency?.createdIds,
        );
        pendingDeletes.remove(competencyId, sentDeleteIds);
      }),
    );
    // The ones that saved get their new ids; one that failed keeps its list
    // as it is, for the retry.
    const savedEdits = Object.fromEntries(
      edited.filter((_edit, index) => results[index].status === "fulfilled"),
    );
    const refreshed = await refetchCompetencies();
    const savedCriteria = savedCriteriaByCompetencyId(refreshed.data);
    setCriteriaEdits((prev) =>
      keepBlankCriteriaAfterSave(
        prev,
        savedEdits,
        savedCriteria,
        isEmptyValue,
        pendingDeletes.savedIds(),
      ),
    );
    // A failure rejects, which autosave shows as an error with Retry.
    const failed = results.find((result) => result.status === "rejected");
    if (failed) {
      throw failed.reason;
    }
  }, [
    competenciesData,
    criteriaEdits,
    pendingDeletes,
    refetchCompetencies,
    saveCompetency,
  ]);

  const handleCriteriaChange = useCallback(
    (competencyId: number, criteria: CompetencyCriteriaInputWithUUID[]) => {
      setIsFormDirty(true);
      setCriteriaEdits((prev) => ({ ...prev, [competencyId]: criteria }));
    },
    [],
  );

  // The bin button, by the criterion's key: one a save in flight is creating
  // gets its id from that save.
  const handleDeleteCriteria = useCallback(
    (competencyId: number, uuid: string) => {
      setIsFormDirty(true);
      const saved =
        savedCriteriaByCompetencyId(competenciesData).get(competencyId) ?? [];
      pendingDeletes.add(String(competencyId), [
        {
          uuid,
          id: (criteriaEdits[competencyId] ?? saved).find(
            (item) => item.uuid === uuid,
          )?.id,
        },
      ]);
      // From what's there now, not when the delete was asked for: a save may
      // have given other new criteria their ids since.
      setCriteriaEdits((prev) => ({
        ...prev,
        [competencyId]: (prev[competencyId] ?? saved).filter(
          (item) => item.uuid !== uuid,
        ),
      }));
    },
    [competenciesData, criteriaEdits, pendingDeletes],
  );

  // Only the first load blanks the page: a refetch after saving keeps what's
  // on screen, so saving doesn't flash the whole table.
  // The pinned level header's height, so the section title row can pin just
  // below it.
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    const thead = theadRef.current;
    if (!thead) {
      return;
    }
    const observer = new ResizeObserver(() =>
      setHeaderHeight(thead.getBoundingClientRect().height),
    );
    observer.observe(thead);
    return () => observer.disconnect();
  }, [isCompetenciesLoading, isCareerTracksLoading]);

  const loading =
    (isCompetenciesLoading && !competenciesData) ||
    (isCareerTracksLoading && !careerTracksData);

  // Edits here save as you go; the competencies already exist.
  const {
    status: autosaveStatus,
    scheduleSave,
    flushSave,
  } = useAutosave({ save: handleSave, enabled: true });
  useEffect(() => {
    if (isFormDirty) {
      scheduleSave();
    }
  }, [isFormDirty, criteriaEdits, scheduleSave]);

  const [deleteCareerLevel] = useMutation<
    DeleteOrgWideCareerLevelMutation,
    DeleteOrgWideCareerLevelMutationVariables
  >(deleteCareerLevelMutation);
  const departmentTitle = teamTitle ?? label("team", { capitalize: true });
  // What this page shows, by the names its sections go by: a department's
  // page shows the company-wide ones under its own.
  const pageTitle = teamId
    ? `${departmentTitle} and Company-Wide ${competenciesLabel}`
    : showAllCompetencies
      ? `All ${competenciesLabel}`
      : `Company-Wide ${competenciesLabel}`;

  // The level whose trash was pressed, with what each choice would delete.
  const [levelDeletion, setLevelDeletion] = useState<{
    level: number;
    deleted: ReadonlyArray<string>;
    toClear: ReadonlyArray<{ id: number; title: string }>;
  } | null>(null);

  const handleDeleteLevel = (level: number) => {
    // Every shared competency loses the level, not just this page's section.
    const { deleted } = summarizeLevelDeletion({
      level,
      competencies: competenciesData
        ? assertEdgesNonNull(competenciesData.competencies)
        : [],
      careerTracks: careerTracksData
        ? assertEdgesNonNull(careerTracksData.careerTracks)
        : [],
      criteriaEdits,
      label,
    });
    // Clearing here only reaches criteria written per level: ones written per
    // role (shown in the all view) belong to the roles.
    const toClear = competencies
      .filter(
        (competency) =>
          competency.criteriaUniqueness ===
            CompetencyCriteriaUniqueness.SameAcrossRoles &&
          (criteriaByCompetencyId[competency.id] ?? []).some(
            (criteria) => criteria.level === level,
          ),
      )
      .map(({ id, title }) => ({ id, title }));
    setLevelDeletion({ level, deleted, toClear });
  };

  // Any edit still in the boxes is about to be rewritten, so it's saved first
  // (after any save already running). If that fails, nothing is deleted.
  const saveBeforeDeleting = async (level: number) => {
    setLevelDeletion(null);
    if (isChangingLevelRef.current) {
      return false;
    }
    setChangingLevel(true);
    if (await flushSave()) {
      return true;
    }
    setChangingLevel(false);
    errorNotificationVar({
      title: `Level ${level} wasn't deleted: your latest changes couldn't be saved.`,
    });
    return false;
  };

  // Only this page's criteria go: the level stays in the ladder, and
  // nothing else moves.
  const handleClearLevel = async (
    level: number,
    clearIds: ReadonlyArray<number>,
  ) => {
    if (!(await saveBeforeDeleting(level))) {
      return;
    }
    try {
      await clearLevel(level, clearIds);
    } finally {
      setChangingLevel(false);
    }
  };
  const clearLevel = async (level: number, clearIds: ReadonlyArray<number>) => {
    // The ids come from what's saved now: this page's copy can predate
    // criteria added since on another page.
    const fresh = await refetchCompetencies();
    const freshCompetencies = assertEdgesNonNull(fresh.data.competencies);
    await Promise.all(
      clearIds.map((competencyId) => {
        const current = freshCompetencies.find(({ id }) => id === competencyId);
        // Switched to per-role meanwhile: its criteria are the roles' now,
        // which this doesn't touch.
        if (
          current?.criteriaUniqueness !==
          CompetencyCriteriaUniqueness.SameAcrossRoles
        ) {
          return null;
        }
        const deleteCriteriaIds = assertEdgesNonNull(current.criteria)
          .filter((criteria) => criteria.level === level)
          .map(({ id }) => id);
        return deleteCriteriaIds.length === 0
          ? null
          : saveCompetency({
              variables: { competencyId, deleteCriteriaIds },
            });
      }),
    );
    // Only the competencies cleared: their lists here name criteria that
    // are gone.
    setCriteriaEdits((prev) => omit(prev, clearIds));
    pendingDeletes.clear((list) => clearIds.map(String).includes(list));
    await refetchCompetencies();
    successNotificationVar({
      title: `Level ${level} cleared from ${pageTitle}`,
    });
  };

  // A level is the rung shared criteria are written against, so it only exists
  // organization-wide: deleting one takes it out of every career track.
  const handleDeleteLevelEverywhere = async (level: number) => {
    if (!(await saveBeforeDeleting(level))) {
      return;
    }
    try {
      await deleteCareerLevel({ variables: { organizationId, level } });
      // Every level above moved down, so every list here is out of date.
      // Everything was saved first, and nothing could be typed since.
      setCriteriaEdits({});
      pendingDeletes.clear(() => true);
      // A level only shown, never written on, goes with the column: the
      // others stay, one lower.
      setMinLevelCount((count) => Math.max(0, count - 1));
      await Promise.all([refetchCompetencies(), refetchCareerTracks()]);
      successNotificationVar({ title: `Level ${level} deleted` });
    } finally {
      setChangingLevel(false);
    }
  };

  // Same menu as a career track, minus what only a track has (its details,
  // and deleting it).
  const viewMenuOptions: DropdownOptionType[] = compact([
    canCreate && {
      label: "Add Level",
      icon: <PlusIcon className="h-4 w-4" />,
      onClick: () => setMinLevelCount(levelCount + 1),
    },
    canCreateHere && {
      label: `Create New ${label("competency", { capitalize: true })}`,
      icon: <ViewGridAddIcon className="h-4 w-4" />,
      onClick: () =>
        setCreatingCompetency({
          title: "",
          // In a department's view a new one belongs to that department.
          scope: teamId ? "shared" : "company",
        }),
    },
    { type: "divider" as const },
    canManageLadder && {
      label: `Create New ${label("team", { capitalize: true })}`,
      icon: <OfficeBuildingIcon className="h-4 w-4" />,
      onClick: () => setIsCreatingDepartment(true),
    },
    canCreate && {
      label: "Create New Track",
      icon: <PlusCircleIcon className="h-4 w-4" />,
      onClick: () => link.redirect(newCareerTrackPath(organizationId)),
    },
  ]);

  return (
    <div>
      {levelDeletion && (
        <LevelDeleteChoiceDialog
          level={levelDeletion.level}
          pageTitle={pageTitle}
          toClear={levelDeletion.toClear}
          deleted={levelDeletion.deleted}
          onClear={() =>
            handleClearLevel(
              levelDeletion.level,
              levelDeletion.toClear.map(({ id }) => id),
            )
          }
          onDeleteEverywhere={() =>
            handleDeleteLevelEverywhere(levelDeletion.level)
          }
          onCancel={() => setLevelDeletion(null)}
        />
      )}
      {isCreatingDepartment && (
        <TeamEditDialog
          organization={{ id: organizationId }}
          onClose={() => setIsCreatingDepartment(false)}
          // A new department has nothing in it yet, so its own table is the
          // useful place to land.
          onTeamSaved={() => setIsCreatingDepartment(false)}
        />
      )}
      <Prompt
        when={isFormDirty || autosaveStatus === "error"}
        message="Are you sure you want to leave? The changes you made will be lost."
      />
      {creatingCompetency !== null && (
        <CompetencyFormModal
          organizationId={organizationId}
          startingData={{
            title: creatingCompetency.title,
            criteriaUniqueness: CompetencyCriteriaUniqueness.SameAcrossRoles,
          }}
          startingTeams={
            creatingCompetency.scope === "shared" && teamId
              ? [{ id: teamId, title: teamTitle ?? "" }]
              : undefined
          }
          onClose={() => setCreatingCompetency(null)}
          onCompetencySaved={() => {
            setCreatingCompetency(null);
            refetchCompetencies();
          }}
        />
      )}
      {editingCompetencyId !== null && (
        <CompetencyFormModal
          organizationId={organizationId}
          competencyId={editingCompetencyId}
          onClose={() => setEditingCompetencyId(null)}
          onCompetencySaved={() => {
            // The dialog saved its own criteria, from what was saved: a list
            // kept here would undo its edits on the next autosave.
            setCriteriaEdits((prev) => omit(prev, editingCompetencyId));
            pendingDeletes.clear(
              (list) => list === String(editingCompetencyId),
            );
            setEditingCompetencyId(null);
            refetchCompetencies();
          }}
          onCompetencyDeleted={() => {
            // Its edits would fail every later autosave.
            setCriteriaEdits((prev) => omit(prev, editingCompetencyId));
            pendingDeletes.clear(
              (list) => list === String(editingCompetencyId),
            );
            setEditingCompetencyId(null);
            refetchCompetencies();
          }}
          showSharedWarning
        />
      )}

      <SettingsPageHeader
        breadcrumbs={getRolesAndCompetenciesBreadcrumbs(
          organizationId,
          label,
          teamId ? departmentTitle : pageTitle,
        )}
      >
        <AutosaveStatus status={autosaveStatus} onRetry={flushSave} />
        {askAiSidebar.isAvailable && <BuildWithAiButton />}
      </SettingsPageHeader>

      {loading && (
        <div className="flex justify-center mt-8 mb-12">
          <Loading />
        </div>
      )}

      {!loading && (
        <div
          className={classNames(
            "flex flex-col h-[calc(100vh-7rem)] -mb-12",
            askAiSidebar.contentClassName,
          )}
        >
          {/* What's being viewed comes first, then how you're looking at it,
              then what you can do to it. */}
          <div className="flex items-center gap-4 mb-3 shrink-0">
            <CareerTrackViewSwitcher
              organizationId={organizationId}
              teamId={teamId}
              showAllCompetencies={showAllCompetencies}
            />
            {canCreate && (
              <div className="shrink-0">
                <EditModeToggle
                  isEditing={isEditing}
                  onChange={async (next) => {
                    // Leaving edit mode waits for the last save to land.
                    if (!next && !(await flushSave())) {
                      return;
                    }
                    setIsEditing(next);
                  }}
                />
              </div>
            )}
            {/* Beside what's being viewed; only while editing. */}
            {isEditing && (
              <div className="shrink-0">
                <Dropdown
                  align="start"
                  options={viewMenuOptions}
                  disabled={isChangingLevel}
                >
                  <Button
                    size={ButtonSize.medium}
                    disabled={isChangingLevel}
                    aria-label="All roles: Actions"
                  >
                    Actions
                    <TopicflowIcon type="horizontalDots" className="h-4 w-4" />
                  </Button>
                </Dropdown>
              </div>
            )}
          </div>

          {!teamId &&
            careerTracksData &&
            careerTracksData.careerTracks.edges.length === 0 && (
              <NoTracksNotice organizationId={organizationId} />
            )}

          <div
            className={classNames(
              "w-full flex-1 min-h-0 mb-6 rounded-md border overflow-hidden bg-white",
            )}
          >
            <div className="relative overflow-auto h-full">
              <table className="text-sm table-fixed h-full min-w-full">
                <thead ref={theadRef}>
                  <tr>
                    <th
                      className={classNames(
                        stickyColWidth,
                        "border border-l-0 border-t-0 sticky left-0 top-0 z-30 bg-gray-100",
                        stickyHeaderBorderClassName,
                      )}
                    >
                      <StickyColumnShadow />
                    </th>
                    {roleLevelIterator(levelCount).map((level) => (
                      <th
                        key={level}
                        className={classNames(
                          roleColWidth,
                          // One line of content, so it sits centred rather
                          // than pinned to the top like a track's header.
                          "group/level px-4 py-3 border border-t-0 text-left font-normal align-middle sticky top-0 z-20 bg-gray-100",
                          stickyHeaderBorderClassName,
                        )}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-xs font-medium text-gray-500">
                            Level {level}
                          </div>
                          {isEditing && canManageLadder && (
                            <Button
                              theme={buttonTheme.iconGray}
                              icon={TrashIcon}
                              size={ButtonSize.medium}
                              className="opacity-0 transition-opacity group-hover/level:opacity-100 focus:opacity-100"
                              disabled={isChangingLevel}
                              onClick={() => handleDeleteLevel(level)}
                              tooltip="Delete level"
                              aria-label={`All roles: Delete level ${level}`}
                            />
                          )}
                        </div>
                      </th>
                    ))}
                    <th
                      className={classNames(
                        addLevelColClassName,
                        // The header row draws its own bottom line and has no
                        // top line, like the cells beside it.
                        "border-t-0 px-4 py-3 align-middle sticky top-0 z-20 bg-gray-100",
                        stickyHeaderBorderClassName,
                      )}
                    >
                      {isEditing && (
                        <div className="flex">
                          <Button
                            theme={buttonTheme.iconGray}
                            icon={TopicflowIconMap.plus}
                            size={ButtonSize.medium}
                            disabled={isChangingLevel}
                            onClick={() => setMinLevelCount(levelCount + 1)}
                            tooltip="Add level"
                            aria-label="All roles: Add level"
                          />
                        </div>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sections.map((section) => (
                    <Fragment key={section.key}>
                      {isEditing && (
                        <TitleRow
                          stickyTop={headerHeight}
                          levelCount={levelCount}
                          title={section.title}
                          toneClassName={sectionHeaderToneClassName}
                          roleLevels={roleLevelIterator(levelCount)}
                          // Outside a department, only a company-wide one
                          // can be made here: a new one from the Single-Track
                          // or Shared header has nothing to scope it to, and
                          // would land under Company-Wide.
                          action={
                            // A department lead makes them only in their own
                            // departments, so never a company-wide one.
                            (
                              section.key === "company"
                                ? canManageLadder
                                : !!teamId && canCreateHere
                            ) ? (
                              <Button
                                theme={buttonTheme.iconGray}
                                icon={TopicflowIconMap.plus}
                                size={ButtonSize.medium}
                                className="-my-1"
                                tooltip={`Add ${label("competency")}`}
                                aria-label={`All roles: Add ${section.key} competency`}
                                onClick={() =>
                                  setCreatingCompetency({
                                    title: "",
                                    scope: section.key,
                                  })
                                }
                              />
                            ) : undefined
                          }
                        />
                      )}
                      {/* An empty section says so, as on a track's table. */}
                      {isEditing && section.competencies.length === 0 && (
                        <tr
                          className={bodyRowLineClassName}
                          aria-label={`All roles: Empty ${section.key} section`}
                        >
                          <td
                            className={classNames(
                              stickyColWidth,
                              "p-4 border border-l-0 sticky left-0 bg-content-background z-10",
                              stickyCellBottomLineClassName,
                            )}
                          >
                            {/* The header's "+" is the way to add one. */}
                            <div className="text-gray-400 text-xs">
                              {section.key === "company"
                                ? `No company-wide ${label("competency", { pluralize: true })} created yet`
                                : teamId
                                  ? `No ${label("competency", { pluralize: true })} created for this ${label("team")}`
                                  : `No ${label("competency", { pluralize: true })} yet`}
                            </div>
                            <StickyColumnShadow />
                          </td>
                          {roleLevelIterator(levelCount).map((level) => (
                            <td
                              key={level}
                              className={classNames(
                                roleColWidth,
                                "border bg-content-background",
                              )}
                            />
                          ))}
                          <td className={addLevelColClassName} />
                        </tr>
                      )}
                      {section.competencies.map((competency) => {
                        const criteria = criteriaByCompetencyId[competency.id];
                        // A department lead edits only what reaches no
                        // further than their departments.
                        const isEditingRow =
                          isEditing && canEditCompetency(competency);
                        return (
                          <tr
                            key={competency.id}
                            className={classNames(
                              "h-52 align-top",
                              bodyRowLineClassName,
                            )}
                          >
                            <td
                              className={classNames(
                                stickyColWidth,
                                "group/competency p-4 border border-l-0 sticky left-0 bg-white z-10",
                                stickyCellBottomLineClassName,
                              )}
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex items-start gap-2 font-medium mt-1">
                                  <ScopeSectionIcon
                                    scope={section.key}
                                    className="mt-0.5"
                                  />
                                  <div className="min-w-0">
                                    {competency.title}
                                    {/* Only while editing, as on a track. */}
                                    {isEditingRow && (
                                      <div>
                                        <AppliesToTag competency={competency} />
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {isEditingRow && (
                                  <Button
                                    theme={buttonTheme.iconGray}
                                    icon={PencilIcon}
                                    size={ButtonSize.medium}
                                    className="opacity-0 transition-opacity group-hover/competency:opacity-100 focus:opacity-100"
                                    disabled={isChangingLevel}
                                    // The dialog opens on what's saved, so edits
                                    // still in the boxes are saved first.
                                    onClick={async () => {
                                      if (!(await flushSave())) {
                                        errorNotificationVar({
                                          title:
                                            "Your latest changes couldn't be saved.",
                                        });
                                        return;
                                      }
                                      setEditingCompetencyId(competency.id);
                                    }}
                                    tooltip={`Edit ${label("competency")}`}
                                    aria-label="All roles: Edit competency"
                                  />
                                )}
                              </div>
                              <StickyColumnShadow />
                            </td>
                            {roleLevelIterator(levelCount).map((level) => {
                              // Written per role: there's no role here to
                              // write them on.
                              if (
                                competency.criteriaUniqueness !==
                                CompetencyCriteriaUniqueness.SameAcrossRoles
                              ) {
                                return (
                                  <td
                                    key={level}
                                    className={classNames(
                                      roleColWidth,
                                      "p-4 border align-middle bg-content-background text-center text-xs text-gray-400",
                                    )}
                                  >
                                    Criteria are written per role, on each
                                    career track
                                  </td>
                                );
                              }
                              const levelCriteria = criteria.filter(
                                (item) => item.level === level,
                              );
                              return (
                                <td
                                  className={classNames(
                                    roleColWidth,
                                    "p-4 border group/cell",
                                    levelCriteria.length === 0 &&
                                      "align-middle bg-content-background",
                                  )}
                                  key={level}
                                >
                                  <CriteriaEditor
                                    activeNote={
                                      <CompetencySharingChip
                                        sharing={getCompetencySharing(
                                          competency,
                                          { label },
                                        )}
                                      />
                                    }
                                    editable={isEditingRow && !isChangingLevel}
                                    revealAddOnHover
                                    emptyState={{
                                      message: "No criteria at this level",
                                      actionText: "Add Criteria",
                                    }}
                                    centerEmptyState
                                    criteria={levelCriteria}
                                    deleteConfirmation={{
                                      title: `Delete ${label(
                                        "competency",
                                      )} criteria?`,
                                      message: `Are you sure you want to delete this criteria? ${
                                        getCompetencySharing(competency, {
                                          label,
                                        }).deleteEffect
                                      }`,
                                    }}
                                    onAddCriteria={() =>
                                      handleCriteriaChange(competency.id, [
                                        ...criteria,
                                        { level, content: "", uuid: uuidv4() },
                                      ])
                                    }
                                    onPasteMultipleCriteria={(items) => {
                                      const updatedUuids = items.map(
                                        ({ uuid }) => uuid,
                                      );
                                      handleCriteriaChange(competency.id, [
                                        ...criteria.filter(
                                          ({ uuid }) =>
                                            !updatedUuids.includes(uuid),
                                        ),
                                        ...items.map((item) => ({
                                          ...criteria.find(
                                            ({ uuid }) => uuid === item.uuid,
                                          ),
                                          level,
                                          content: item.content,
                                          uuid: item.uuid || uuidv4(),
                                        })),
                                      ]);
                                    }}
                                    onDeleteCriteria={(uuid) =>
                                      handleDeleteCriteria(competency.id, uuid)
                                    }
                                    onBlurCriteria={flushSave}
                                    onCriteriaChange={(uuid, content) =>
                                      handleCriteriaChange(
                                        competency.id,
                                        criteria.map((item) =>
                                          item.uuid === uuid
                                            ? { ...item, content }
                                            : item,
                                        ),
                                      )
                                    }
                                  />
                                </td>
                              );
                            })}
                            <td className={addLevelColClassName} />
                          </tr>
                        );
                      })}
                    </Fragment>
                  ))}

                  {/* Takes the table's spare height, so no row — not even a
                      section header — stretches to fill the page. */}
                  <tr className="h-full">
                    <td
                      className={classNames(
                        stickyColWidth,
                        "border border-l-0 sticky left-0 z-10 bg-content-background",
                      )}
                    >
                      <StickyColumnShadow />
                    </td>
                    {roleLevelIterator(levelCount).map((level) => (
                      <td
                        key={level}
                        className={classNames(
                          roleColWidth,
                          "border bg-content-background",
                        )}
                      />
                    ))}
                    <td className={addLevelColClassName} />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllRolesCompetencies;
